import React, { useEffect, useState } from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Formik } from 'formik-v2';
import * as Yup from 'yup';
import { ActionButton } from '@ax2/common/components/ActionButton';
import styles from '../styles/pages/SetNewPassword.module.css';
import formStyles from '../styles/components/form/Forms.module.css';
import base from '../styles/Base.module.css';
import DefaultPage from './DefaultPage';
import PasswordField from '../components/form/PasswordField';
import {
  setNewPassword,
  validatePasswordResetToken,
} from '../rest/passwordResetClient';
import TokenValidationRequest from '../model/password-reset/TokenValidationRequest';
import NewPasswordRequest from '../model/password-reset/NewPasswordRequest';

interface SetNewPasswordFormValues {
  password: string;
  repeatPassword: string;
}

const setNewPasswordSchema = Yup.object<SetNewPasswordFormValues>().shape({
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
  repeatPassword: Yup.string()
    .oneOf(
      [Yup.ref('password'), null],
      'Password confirmation must match the password'
    )
    .required('Password confirmation is required'),
});

export interface SetNewPasswordFormProps {
  id: string;
  token: string;
  onComplete: () => void;
}

export const SetNewPasswordForm: React.FC<SetNewPasswordFormProps> = ({
  id,
  token,
  onComplete,
}) => {
  const [complete, setComplete] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const initialValues: SetNewPasswordFormValues = {
    password: '',
    repeatPassword: '',
  };

  if (complete) {
    return <div>Your password has been set, you can now sign in.</div>;
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={setNewPasswordSchema}
      onSubmit={(values, { setSubmitting }) => {
        setSubmitting(true);
        setNewPassword(new NewPasswordRequest(id, token, values.password))
          .then(() => {
            setFormError(null);
            setSubmitting(false);
            setComplete(true);
            onComplete();
          })
          .catch(e => {
            setSubmitting(false);
            setFormError(e.description || e.message || e.toString());
          });
      }}
    >
      {({ handleSubmit, isSubmitting }) => (
        <form onSubmit={handleSubmit} className={formStyles.form}>
          <div className={formStyles.formItems}>
            <PasswordField
              name="password"
              label="Password"
              labelNote="required"
              wrapperClassName={formStyles.formItemShorterWidth}
            />
            <PasswordField
              name="repeatPassword"
              label="Repeat Password"
              labelNote="required"
              wrapperClassName={formStyles.formItemShorterWidth}
            />
            {formError ? (
              <div className={formStyles.formError}>{formError}</div>
            ) : null}
            <div className={formStyles.formButtons}>
              <ActionButton
                onClick={() => !isSubmitting && handleSubmit()}
                loading={isSubmitting}
                type="primary"
                text="Set Password"
              />
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
};

const invalidTokenError =
  'Password reset token is invalid or expired. Make sure to use a correct link.';

const SetNewPassword: React.FC<RouteComponentProps> = ({ location }) => {
  const [id, setId] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [validated, setValidated] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [complete, setComplete] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    setId(searchParams.get('id'));
    setToken(searchParams.get('token'));
  }, [location.search]);

  useEffect(() => {
    if (id && token) {
      setValidationError(null);
      validatePasswordResetToken(
        new TokenValidationRequest(id || '', token || '')
      )
        .then(() => {
          setValidated(true);
        })
        .catch(e => {
          if (e.reason && e.reason === 'InvalidPasswordResetToken') {
            setValidationError(invalidTokenError);
          } else {
            setValidationError(e.description || e.message || invalidTokenError);
          }
        });
    } else {
      setValidationError(
        'This page has an invalid URL, some search string parameters are missing'
      );
    }
  }, [id, token]);

  return (
    <DefaultPage header={{ title: 'Set New Password' }}>
      <div className={[base.actionBox, base.actionBoxFullWidth].join(' ')}>
        <p
          className={[
            base.sectionText,
            validationError ? base.errorLabel : styles.text,
          ].join(' ')}
        >
          {validationError
            ? validationError
            : validated
            ? complete
              ? 'Thank you!'
              : 'Set a new password.'
            : 'Loading...'}
        </p>
        {validated && (
          <SetNewPasswordForm
            id={id || ''}
            token={token || ''}
            onComplete={() => setComplete(true)}
          />
        )}
      </div>
    </DefaultPage>
  );
};

export default withRouter(SetNewPassword);
